import { apiClient } from './api';
import {
  ApiResponse,
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  UserApiResponse,
  AuthResponse,
} from '@/types';

interface RegisterApiResponse {
  id: number;
  email: string;
}

interface RefreshTokenResponse {
  access: string;
}

export const authApi = {
  async login(credentials: LoginRequest): Promise<ApiResponse<TokenResponse>> {
    const response = await apiClient.post<TokenResponse>(
      '/auth/token/',
      credentials
    );

    if (response.success && response.data.access && response.data.refresh) {
      apiClient.setTokens(response.data.access, response.data.refresh);
    }

    return response;
  },

  async register(
    userData: RegisterRequest
  ): Promise<ApiResponse<RegisterApiResponse>> {
    return apiClient.post<RegisterApiResponse>('/auth/register/', userData);
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout/');
    } finally {
      apiClient.setTokens(null, null);
    }
  },

  async getCurrentUser(): Promise<ApiResponse<UserApiResponse>> {
    return apiClient.get<UserApiResponse>('/auth/me/');
  },

  async refreshToken(): Promise<ApiResponse<RefreshTokenResponse>> {
    const refreshToken = apiClient.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post<RefreshTokenResponse>(
      '/auth/token/refresh/',
      { refresh: refreshToken }
    );

    if (response.success && response.data.access) {
      apiClient.setTokens(response.data.access, refreshToken);
    }

    return response;
  },

  async verifyEmail(token: string): Promise<ApiResponse<{ detail: string }>> {
    return apiClient.post<{ detail: string }>('/auth/verify-email/', {
      token,
    });
  },

  async resendVerifyEmail(
    email: string
  ): Promise<ApiResponse<{ detail: string }>> {
    return apiClient.post<{ detail: string }>('/auth/verify-email/resend/', {
      email,
    });
  },

  async passwordChange(
    current_password: string,
    new_password: string
  ): Promise<ApiResponse<{ detail: string }>> {
    return apiClient.post<{ detail: string }>('/auth/password/change/', {
      current_password,
      new_password,
    });
  },

  async passwordResetRequest(
    email: string
  ): Promise<ApiResponse<{ detail: string }>> {
    return apiClient.post<{ detail: string }>('/auth/password-reset/request/', {
      email,
    });
  },

  async passwordResetConfirm(
    token: string,
    new_password: string
  ): Promise<ApiResponse<{ detail: string }>> {
    return apiClient.post<{ detail: string }>('/auth/password-reset/confirm/', {
      token,
      new_password,
    });
  },

  async signInWithProvider(
    provider: string,
    token?: string,
    idToken?: string
  ): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>(
      `/auth/social/${provider}`,
      {
        token,
        idToken,
      }
    );

    if (response.success && response.data.token) {
      apiClient.setToken(response.data.token);
    }

    return response;
  },
};
